import React, { useMemo } from 'react';
import { Form, Select, Tooltip } from '@duxton-web/ui';
import _ from 'lodash';
import { useParams } from 'react-router-dom';

import { CountrySelector } from '@/common/components';
import { ConstraintOperator } from '@/common/ConstraintsEditor/Constraint/facetMap';
import CityTreeSelectV2 from '@/common/selects/CityTreeSelect/CityTreeSelectV2';
import CountrySelect from '@/common/selects/CountrySelect';
import RegionSelect from '@/common/selects/RegionSelect';
import {
  getAssignmentStrategy,
  getGeography,
  getRestrictByConstraint,
  isViewMode,
  PopulationTargetName,
  PopulationTargetType,
  RegionalCountries,
} from '@/features/experiment/constants';
import { useExperimentContext } from '@/features/experiment/contexts/experiment-context';

const DynamicTarget = () => {
  const params = useParams<{ action: string }>();
  const action = params.action;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment, configurations } = experimentContext;

  const isAssignmentStrategySelected = !!getAssignmentStrategy(configurations);
  const experimentDomain = experiment?.domain;
  const geography = getGeography(experimentDomain);

  const form = Form.useFormInstance();

  const dynamicTargetOptions = useMemo(() => {
    const selectableTargetTypes = [
      PopulationTargetType.CITY,
      PopulationTargetType.COUNTRY,
      PopulationTargetType.REGIONAL,
    ];
    if (selectableTargetTypes.includes(geography)) {
      return [
        {
          value: geography,
          label: PopulationTargetName[geography],
        },
      ];
    }

    return selectableTargetTypes.map((item) => ({
      value: item,
      label: PopulationTargetName[item],
    }));
  }, [geography]);

  const personalizedByCountry = configurations.every((c) => c.country_code);
  const disablePopulationStepContent =
    !isAssignmentStrategySelected || readOnly || personalizedByCountry;

  const restrictByConstraint = getRestrictByConstraint(experiment) || {
    target: '',
    op:
      geography === 'multi'
        ? ConstraintOperator.IN.value
        : ConstraintOperator.EQUALS.value,
    valueItemType: '',
    value: '',
  };

  let targetType = restrictByConstraint?.target;
  let targetValue: string | string[] = restrictByConstraint.value;

  if (targetType === PopulationTargetType.CITY) {
    // check if cityValues is a stringified array of string
    if (
      typeof targetValue === 'string' &&
      targetValue.startsWith('[') &&
      targetValue.endsWith(']')
    ) {
      targetValue = targetValue.substring(1, targetValue.length - 1).split(',');
    }
  }

  const isCitySelect =
    (geography === PopulationTargetType.CITY || geography === 'multi') &&
    targetType === PopulationTargetType.CITY;

  const isCtrySelect =
    (geography === PopulationTargetType.COUNTRY || geography === 'multi') &&
    targetType === PopulationTargetType.COUNTRY;

  const isRegionOnly = geography === PopulationTargetType.REGIONAL;

  const isRegionMulti =
    targetType === PopulationTargetType.REGIONAL && geography === 'multi';
  const targetValueRules = [
    {
      required: true,
      message: '',
    },
    {
      validator: (_rule, value) => {
        if (Array.isArray(value)) {
          return value.length
            ? Promise.resolve()
            : Promise.reject(new Error('Cannot be empty'));
        }

        if (typeof value === 'string') {
          return value.trim().length
            ? Promise.resolve()
            : Promise.reject(new Error('Cannot be empty'));
        }

        return value
          ? Promise.resolve()
          : Promise.reject(new Error('Cannot be empty'));
      },
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <Tooltip
        title={
          !isAssignmentStrategySelected &&
          'Please select an assignment strategy first!'
        }
        placement="leftTop"
      >
        <Form.Item
          name={['constraints', 0, 'target']}
          style={{ marginBottom: 0 }}
          required
        >
          <Select
            style={{ minWidth: 100 }}
            options={dynamicTargetOptions}
            disabled={disablePopulationStepContent}
            onChange={(value) => {
              form.setFieldValue(
                ['constraints', 0, 'value'],
                value === PopulationTargetType.REGIONAL
                  ? RegionalCountries.map((country) => country)
                  : '',
              );
            }}
          />
        </Form.Item>
      </Tooltip>
      <Form.Item
        name={['constraints', 0, 'op']}
        style={{ marginBottom: 0 }}
        required
      >
        <Select
          options={[
            { value: ConstraintOperator.IN.value, label: 'in' },
            { value: ConstraintOperator.EQUALS.value, label: '=' },
          ]}
          style={{ minWidth: 35 }}
          disabled
        />
      </Form.Item>

      <div style={{ minWidth: 350 }}>
        {isCitySelect && (
          <Form.Item
            name={['constraints', 0, 'value']}
            style={{ marginBottom: 0 }}
            required
            rules={targetValueRules}
          >
            <CityTreeSelectV2
              value={targetValue}
              multiple={geography === 'multi'}
              disabled={disablePopulationStepContent}
              onChange={() => {}}
            />
          </Form.Item>
        )}
        {isCtrySelect && (
          <Form.Item
            name={['constraints', 0, 'value']}
            style={{ marginBottom: 0 }}
            required
            rules={targetValueRules}
          >
            <CountrySelector
              value={targetValue}
              multiple={geography === 'multi'}
              disabled={disablePopulationStepContent}
              // tokenizable
              style={{ width: 500 }}
            />
          </Form.Item>
        )}
        {isRegionMulti && (
          <Form.Item
            name={['constraints', 0, 'value']}
            style={{ marginBottom: 0 }}
            required
            rules={targetValueRules}
          >
            <CountrySelect value={targetValue} disabled multiple />
          </Form.Item>
        )}
        {isRegionOnly && (
          <Form.Item
            name={['constraints', 0, 'value']}
            style={{ marginBottom: 0 }}
            required
            rules={targetValueRules}
          >
            <RegionSelect
              value={targetValue}
              className={`cy-experiment-city-select`}
              disabled={disablePopulationStepContent}
            />
          </Form.Item>
        )}
      </div>
    </div>
  );
};

export default DynamicTarget;
